/**
 * Installation de BIOTRANSAC comme application de bureau (PWA).
 *
 * L'évènement « beforeinstallprompt » survient tôt, souvent avant l'affichage de la barre latérale
 * (connexion Keycloak) : il est capté dès le démarrage et conservé ici.
 */
import { useEffect, useState } from 'react';

interface InvitationInstallation extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let invitation: InvitationInstallation | null = null;
const abonnes = new Set<() => void>();
const notifier = () => abonnes.forEach((f) => f());

/** Application déjà ouverte dans sa propre fenêtre (installée) */
export function estInstallee(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** À appeler une fois au démarrage : service worker (production) et capture de l'invitation */
export function initialiserInstallation(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // le bouton de la barre latérale déclenchera l'invitation
    invitation = e as InvitationInstallation;
    notifier();
  });
  window.addEventListener('appinstalled', () => {
    invitation = null;
    notifier();
  });

  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch((err) => console.warn('[PWA] Service worker non enregistré :', err));
    });
  }
}

/** Bouton « Installer » : disponible tant que le navigateur propose l'installation */
export function useInstallation(): { installable: boolean; installer: () => Promise<void> } {
  const [, forcer] = useState(0);
  useEffect(() => {
    const maj = () => forcer((n) => n + 1);
    abonnes.add(maj);
    return () => {
      abonnes.delete(maj);
    };
  }, []);

  const installer = async () => {
    if (!invitation) return;
    const courante = invitation;
    await courante.prompt();
    await courante.userChoice;
    invitation = null; // une invitation ne peut servir qu'une fois
    notifier();
  };

  return { installable: !!invitation && !estInstallee(), installer };
}
